import PTPEquipmentPanel from '../PTPEquipmentPanel';
import LimitControl from '../LimitControl';
import { GenericScaledField } from 'src/components/controls/rhf/GenericScaledField';
import { round } from 'src/utils/useful_functions';
import {
  PTPFormPanel,
  PTPPanelConfigImpl,
  PTPShowProps,
  syncFormState,
  getEndFromChoices,
  getEquipmentFromChoices,
} from '../utils';
import {
  genericEndPerformanceSummary,
  genericLinkSummary,
  genericEndConfig,
  remoteProductNextValue,
} from './config-utils';

const equipment: PTPFormPanel<PTPShowProps> = {
  kind: 'equipment',
  title: 'Equipment',
  name: 'equipment_link_a',
  component: PTPEquipmentPanel,
  fields: [
    {
      attrName: 'band',
      getter: 'local.radios.0.equipment.band',
    },
    {
      attrName: 'product',
      getter: 'local.radios.0.equipment.product',
    },
    {
      attrName: 'remote_product',
      getter: 'remote.radios.0.equipment.product',
      nextValue: remoteProductNextValue,
    },
    {
      attrName: 'regulation',
      getter: 'local.radios.0.equipment.regulation',
    },
    {
      attrName: 'link_protection',
      getter: 'local.radios.0.equipment.link_protection',
    },
    {
      attrName: 'io_connectivity',
      getter: 'local.radios.0.equipment.io_connectivity',
    },
    {
      attrName: 'sync_input',
      getter: 'local.radios.0.equipment.sync_input',
    },
    {
      attrName: 'dynamic_payload',
      getter: 'local.radios.0.equipment.dynamic_payload',
    },
  ],
};

const config: PTPFormPanel<any> = {
  kind: 'config',
  title: 'Product Configuration',
  name: 'equipment_config_link_a',
  component: PTPEquipmentPanel,
  fields: [
    {
      attrName: 'band_setting',
      getter: 'local.radios.0.equipment.band_setting',
    },
    {
      attrName: 'tr_spacing',
      getter: 'local.radios.0.equipment.tr_spacing',
    },
    {
      attrName: 'bandwidth',
      getter: 'local.radios.0.equipment.bandwidth',
    },
    {
      attrName: 'modulation_type',
      getter: 'local.radios.0.equipment.modulation_type',
    },
    {
      attrName: 'modulation_mode',
      getter: 'local.radios.0.equipment.modulation_mode',
    },
    {
      attrName: 'max_mod_mode',
      getter: 'local.radios.0.equipment.max_mod_mode',
    },
    {
      attrName: 'min_mod_mode',
      getter: 'local.radios.0.equipment.min_mod_mode',
    },
    {
      attrName: 'hop_pattern',
      getter: 'local.radios.0.equipment.hop_pattern',
    },
    {
      attrName: 'mms_hop_offset',
      getter: 'local.radios.0.equipment.mms_hop_offset',
    },
    {
      attrName: 'polarization',
      getter: 'local.radios.0.equipment.polarization',
    },
    {
      attrName: 'master',
      getter: 'local.radios.0.equipment.master',
    },
    {
      attrName: 'hi',
      getter: 'local.radios.0.equipment.hi',
    },
  ],
};

// The calculated power is displayed in dBm like the other products,
// but the cnReach user limit is configured in mW
// (product.power_units == "mW")
function cnReachMaxPower(endName: string) {
  return {
    label: 'User Limit',
    maxValueLabel: 'Maximum Power',
    component: LimitControl,
    attrName: `${endName}_power_limit`,
    maxValueGetter: `${endName}.radios.0.power.power`,
    maxValueFormatter: (value) => round(parseFloat(value), 1),
    maxValueUnits: 'dBm',
    maxValuePrecision: 1,
    units: 'mW',
    precision: 0,
    checkboxGetter: `${endName}.radios.0.power.use_user_power`,
    valueGetter: `${endName}.radios.0.power.user_power_mw`,
    min: 1,
    max: 10000,
    reCalculate: true,
    defaultValue({ formGetter }) {
      const powerMw = formGetter(`${endName}.radios.0.power.power_mw`);
      return powerMw != null ? round(powerMw, 0) : 1000;
    },
  };
}

function maxPayloadBytesLimits(choices, endName: string) {
  const limits = getEndFromChoices(choices, endName)?.max_payload_bytes_limits;
  return {
    min: limits?.min ?? 64,
    max: limits?.max ?? 1600,
  };
}

function maxPayloadBytes(endName: string) {
  return {
    label: 'Max Payload Bytes',
    attrName: `${endName}_max_payload_bytes`,
    getter: `${endName}.radios.0.power.max_payload_bytes`,
    component: GenericScaledField,
    // include the space here so that it appears in the tooltip
    units: ' Bytes',
    precision: 0,
    reCalculate: true,
    componentProps({ choices }: PTPShowProps) {
      return maxPayloadBytesLimits(choices, endName);
    },
    nextValueExtra(newChoices, getValues, setValue) {
      // populate the form when switching to cnReach from another
      // product, otherwise the empty value fails the "required"
      // validation and blocks the Apply button
      const getter = `${endName}.radios.0.power.max_payload_bytes`;
      const current = getValues(getter);
      if (current == null || current === '') {
        const { max } = maxPayloadBytesLimits(newChoices, endName);
        setValue(getter, max, { shouldDirty: true, shouldValidate: true });
      }
    },
  };
}

function cnReachEndConfig(endName: string) {
  const panel = genericEndConfig(endName, { cableChoices: true });

  const powerIdx = panel.fields.findIndex(
    (f) => f.attrName === `${endName}_power_limit`
  );
  if (powerIdx >= 0) {
    panel.fields[powerIdx] = cnReachMaxPower(endName);
  }

  const txFreqIdx = panel.fields.findIndex(
    (f) => f.attrName === `${endName}_tx_frequency`
  );
  if (txFreqIdx >= 0) {
    // frequency selection only applies to the licensed band settings
    const baseShow = panel.fields[txFreqIdx].show;
    panel.fields[txFreqIdx] = {
      ...panel.fields[txFreqIdx],
      show(args: PTPShowProps) {
        const bandSetting = getEquipmentFromChoices(args.choices)?.band_setting
          ?.value;
        if (bandSetting === 'ISM') {
          return false;
        }
        return baseShow == null || baseShow(args);
      },
    };
  }
  panel.fields.splice(
    txFreqIdx >= 0 ? txFreqIdx + 1 : panel.fields.length,
    0,
    maxPayloadBytes(endName)
  );

  return panel;
}

const PTPcnReachPanelConfig: PTPPanelConfigImpl = {
  syncFormState,
  equipment: [equipment, config],
  performance: {
    summary: {
      link: [genericLinkSummary()],
      local: genericEndPerformanceSummary('local'),
      remote: genericEndPerformanceSummary('remote'),
    },
  },
  ends: {
    title: 'Configuration at Each End',
    local: cnReachEndConfig('local'),
    remote: cnReachEndConfig('remote'),
  },
};

export default PTPcnReachPanelConfig;
